import React, { useMemo, useState } from 'react';
import { Box } from '@common-fe/common-fe';

import { FilterSetters } from '@/utils/hooks/useFilters';

import { ReportTab, ReportTableBaseProps, ReportTypeKeys } from '../../report.types';
import { AddScheduledReportButton } from '../../ScheduledReport';
import EmployerSelector from '../EmployerSelector';
import ExportButton from '../ExportButton';
import { useIsEmployerIsPresent } from '../hooks';
import PlaceholderBase from '../PlaceholderBase';
import PlanYearSelector from '../PlanYearSelector';
import ReportLastUpdateDate from '../ReportLastUpdateDate';
import { ReportSummaryBase, ReportSummaryWrapper } from '../ReportSummary';
import TopButtonPortalWrapper from '../TopButtonPortalWrapper';

import { HealthPlanEnrollmentsReportParams } from './HealthPlanEnrollmentReport.types';
import {
  useHealthPlanEnrollmentsReportExportSummaryCSVQuery,
  useHealthPlanEnrollmentsReportSummaryQuery,
} from './queries';

interface Props extends ReportTableBaseProps {
  filtersValues: HealthPlanEnrollmentsReportParams;
  onSetFilterValue: FilterSetters<HealthPlanEnrollmentsReportParams>;
}

const HealthPlanEnrollmentsReportSummary: React.FC<Props> = ({
  filtersValues,
  onSetFilterValue,
  isPreviewMode,
  defaultEmployerId,
}) => {
  const [isAutoFocus, setAutoFocus] = useState(false);
  const isEmployerIsPresent = useIsEmployerIsPresent(filtersValues);
  const { isLoading, data, lastTimeUpdateDate, isEmptyData, isLoadingMore, isHasNextPage, handleLoadMore } = useHealthPlanEnrollmentsReportSummaryQuery({
    ...filtersValues,
  });
  const { handleExport: handleExportCSV, isLoading: isExportLoading } =
    useHealthPlanEnrollmentsReportExportSummaryCSVQuery({
      ...filtersValues,
    });

  const childrenNode = useMemo(() => {
    if (!isEmployerIsPresent) {
      return (
        <PlaceholderBase>
          <PlaceholderBase.Link onClick={() => setAutoFocus(true)} />
        </PlaceholderBase>
      );
    }
    if (!isLoading && !data.length) {
      return (
        <PlaceholderBase>
          <PlaceholderBase.Default />
        </PlaceholderBase>
      );
    }

    return <ReportSummaryBase isPreviewMode={isPreviewMode} isLoading={isLoading} data={data} onLoadMore={handleLoadMore} isHasMore={isHasNextPage} isLoadingMore={isLoadingMore} />;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, filtersValues.employerId, filtersValues.employerIds, isLoading, isPreviewMode]);
  return (
    <TopButtonPortalWrapper
      isPreviewMode={isPreviewMode}
      infoNode={
        <ReportLastUpdateDate
          isLoading={isLoading}
          key={`${filtersValues.employerId}`}
          value={lastTimeUpdateDate}
        />
      }
      buttonNode={
        <Box direction="row">
          <AddScheduledReportButton
            employerId={filtersValues.employerId}
            type={ReportTypeKeys.HealthPlanEnrollment}
            tab={ReportTab.Summary}
          />
          <Box margin={{ left: 'spacing12' }}>
            <ExportButton
              onClick={handleExportCSV}
              isLoading={isExportLoading}
              disabled={!isEmployerIsPresent || isEmptyData}
              text="Download Summary (CSV)"
            />
          </Box>
        </Box>
      }
    >
      <ReportSummaryWrapper
        isPreviewMode={isPreviewMode}
        filters={
          <Box fill direction="row">
            <Box width="290px">
              <EmployerSelector
                disabled={isPreviewMode && !!defaultEmployerId}
                onSetAutoFocus={setAutoFocus}
                isAutoFocus={isAutoFocus}
                value={filtersValues.employerId}
                onChangeValue={onSetFilterValue.employerId}
              />
            </Box>

            <Box width="290px" margin={{ left: 'spacing12' }}>
              <PlanYearSelector
                disabled={!isEmployerIsPresent}
                value={filtersValues.planYearPeriod}
                onChangeValue={onSetFilterValue.planYearPeriod}
              />
            </Box>
          </Box>
        }
      >
        {childrenNode}
      </ReportSummaryWrapper>
    </TopButtonPortalWrapper>
  );
};

export default HealthPlanEnrollmentsReportSummary;
